import { IonIcon } from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { OrganizationSwitcher } from "@/components/molecules/organization/organization-switcher";
import { SignedQueryImage } from "@/components/molecules/signed-query-image";
import { chevronDownIcon } from "@/lib/icons/chevron-down.icon";
import { useMyOrganizations } from "@/lib/query/organization/use-my-organizations";
import { queryOptions_signed_key_public_by_slug } from "@/lib/requests/file/file.get-signed-key";

/**
 * De organisatie waar je nu in zit, rechtsboven op elk tabblad.
 *
 * Twee dingen zitten hier bewust in het component en niet in de schermen die hem plaatsen:
 *
 * `ml-auto` -- hij duwt zichzelf naar de rechterrand van de rij waar hij in staat. Dat is de enige
 * reden dat "altijd rechtsboven" ook waar blijft als een scherm er later een knop naast zet: wat er
 * links van hem gebeurt kan hem niets schelen, hij pakt de ruimte die overblijft. Stond dit in de
 * schermen, dan was het acht plekken die het alle acht moeten onthouden.
 *
 * `touch-target` — het pilletje is een slag kleiner dan een titel zodat het die niet overstemt, en
 * dat is te klein voor een vinger. De gedeelde utility legt er een onzichtbaar vlak van minstens
 * 44pt overheen; zie `styles/tailwind/utilities.css`.
 *
 * Het logo staat in een vierkant kader en niet in een bolletje. Een ronde uitsnede knipt de hoeken
 * van een merk af, en juist daar staat bij de meeste klanten hun naam of beeldmerk.
 * `object-contain` hoort erbij: het kader kadert niet, het past het logo in. Vierkant is ook wat de
 * wisselaar al deed -- dit was de enige plek waar hetzelfde plaatje er anders uitzag dan in de
 * lijst waar je hem kiest.
 */
export function OrganizationDropdown() {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const { data: organizationsData } = useMyOrganizations();

  const organizations = organizationsData?.organizations ?? [];
  const hasMultipleOrganizations = organizations.length > 1;
  const organizationSlug = import.meta.env.VITE_ORGANIZATION_SLUG;
  const shouldShowDropdown =
    hasMultipleOrganizations && (!organizationSlug || organizationSlug === "boomer");

  const activeOrganization = organizations.find(
    (org) => org.id === organizationsData?.activeOrganizationId,
  );

  if (!activeOrganization) {
    return null;
  }

  // Wisselen kan: het logo krijgt een rand en een chevron. Die twee samen zijn op iOS het teken dat
  // er iets opengaat als je erop drukt -- een kaal logo is een merk, en daar drukt niemand op.
  if (shouldShowDropdown) {
    return (
      <>
        <button
          type="button"
          onClick={() => modalRef.current?.present()}
          aria-label={t("common:settings.organization.current.label", {
            name: activeOrganization.name ?? "",
          })}
          aria-haspopup="dialog"
          className="touch-target ml-auto flex shrink-0 items-center gap-1 rounded-full border border-bordercolor-primary bg-surface-primary py-1.5 pl-1.5 pr-2 transition-opacity active:opacity-70"
        >
          {activeOrganization.slug && (
            <SignedQueryImage
              width={24}
              height={24}
              alt=""
              className="size-6 rounded-md object-contain"
              {...queryOptions_signed_key_public_by_slug([
                "android-chrome-512x512.png",
                { slug: activeOrganization.slug },
              ])}
            />
          )}
          <IonIcon icon={chevronDownIcon} className="text-sm text-txt-tertiary" />
        </button>

        <OrganizationSwitcher ref={modalRef} />
      </>
    );
  }

  // Er valt niets te wisselen (eigen merk-app, of maar één organisatie). Dan ook geen rand en geen
  // chevron: dat zou een knop beloven die er niet is. Alleen het logo, als merk.
  if (activeOrganization.slug) {
    return (
      <div className="ml-auto size-7 shrink-0 overflow-hidden rounded-md">
        <SignedQueryImage
          width={28}
          height={28}
          alt={activeOrganization.name ?? "Organization Logo"}
          className="size-full object-contain"
          {...queryOptions_signed_key_public_by_slug([
            "android-chrome-512x512.png",
            { slug: activeOrganization.slug },
          ])}
        />
      </div>
    );
  }

  return null;
}
